/**
 * **What a client would see** — the Ustawienia tab's phone (T-B1): the client's booking screen,
 * drawn from the trainer's own hours and policy, so every change on the tab shows its consequence.
 *
 * ── It is drawn from the TRAINER's slot read, and that is a real difference ──────────────────
 * The trainer cannot ask the client's question: `GET /booking/slots` is the client's route. What the
 * trainer can read is `GET /trainer-availability/slots`, the same derivation (R5) — the grid, the
 * overrides, the blocks, the busy pool, the length, the step and the buffer — for the audience
 * `SlotAudience.Trainer`, which the minimum notice and the horizon do NOT bound (R1 bounds how close
 * and how far a BOOKING PARTY may reach). So those two are applied here, off the policy on screen,
 * by the server's own rules (`SlotCalculator`): a start earlier than now plus the notice is dropped,
 * and so is one on a trainer's day past today plus the horizon — the last day itself is whole. What a client's read carries that the trainer's cannot — their own last
 * booking („Jak ostatnio") and their credit balance — is not drawn: this is nobody in particular.
 *
 * ── The digit is the only thing that speaks ──────────────────────────────────────────────────
 * Mobile's picker rule (`slotDays`): a day shows its number and nothing else, and the number's ink
 * says how much is free — plenty, few (two starts or fewer, the owner's threshold of 13 September
 * 2026), none — never why a day is empty.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type {
  TrainWithMeModulesBookingDtosResponsesBookingPolicyResponse as BookingPolicy,
  TrainWithMeModulesBookingDtosResponsesBookingSlotsResponse as SlotsResponse,
} from '~/shared/api/generated/model';
import { parseInstant } from '~/shared/lib/calendarDate';

import { mondayOfDay, shiftDay, wallReading, type CivilDay } from './trainerClock';

/** Two starts or fewer is „mało miejsc" — mobile's `FEW_FREE_HOURS`. */
export const FEW_FREE_STARTS = 2;

const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;

export type PreviewDensity = 'many' | 'few' | 'none' | 'past';

export interface PreviewCell {
  readonly day: CivilDay | null;
  readonly density: PreviewDensity;
  readonly selected: boolean;
}

export interface PreviewStart {
  readonly startMs: number;
  /** Minutes since the day's midnight on the trainer's clock. */
  readonly minutes: number;
}

export type ClientPreview =
  /** Self-booking is off: a client is told the trainer sets the times, and shown no hour. */
  | { readonly kind: 'off' }
  /** Self-booking is on, and nothing inside the notice and the horizon is free. */
  | { readonly kind: 'empty' }
  /**
   * Self-booking is on, but the trainer has no stored zone: the server offers no slot at all
   * (R3 — the week's hours mean nothing without the clock they are read on).
   */
  | { readonly kind: 'noZone' }
  | {
      readonly kind: 'ready';
      /** The first day of the month drawn. */
      readonly month: CivilDay;
      readonly weeks: readonly (readonly PreviewCell[])[];
      readonly day: CivilDay;
      readonly starts: readonly PreviewStart[];
      readonly start: PreviewStart;
      readonly endMs: number;
      /** When a cancel stops being free, or `null` when clients may not cancel (R12). */
      readonly cancelFreeUntilMs: number | null;
    };

export interface PreviewInput {
  readonly slots: SlotsResponse;
  readonly policy: BookingPolicy;
  readonly nowMs: number;
  readonly zone: string;
  /** Whether the trainer's zone is stored, rather than the server's default standing in. */
  readonly zonePublished: boolean;
  readonly today: CivilDay;
}

/** Every start a client could book right now, by day, in order: the notice and the horizon applied. */
export function bookableStarts(input: PreviewInput): Map<CivilDay, PreviewStart[]> {
  const earliest = input.nowMs + (input.policy.minimumNoticeHours ?? 0) * HOUR_MS;
  // `SlotCalculator.LastDateOnOffer`: the horizon counts the trainer's own civil days from today,
  // and its last day is on offer WHOLE.
  const lastDate = shiftDay(input.today, input.policy.horizonDays ?? 0);
  const byDay = new Map<CivilDay, PreviewStart[]>();
  for (const day of input.slots.days ?? []) {
    for (const entry of day.starts ?? []) {
      const startMs = parseInstant(entry.start);
      if (startMs === null || startMs < earliest) continue;
      const reading = wallReading(startMs, input.zone);
      if (reading.day > lastDate) continue;
      byDay.set(reading.day, [
        ...(byDay.get(reading.day) ?? []),
        { startMs, minutes: reading.minutes },
      ]);
    }
  }
  for (const starts of byDay.values()) starts.sort((a, b) => a.startMs - b.startMs);
  return byDay;
}

/** The Monday-first weeks a month's days fall in. */
function monthWeeks(month: CivilDay): CivilDay[][] {
  const weeks: CivilDay[][] = [];
  for (
    let monday = mondayOfDay(month);
    monday.slice(0, 7) <= month.slice(0, 7);
    monday = shiftDay(monday, 7)
  ) {
    weeks.push(Array.from({ length: 7 }, (_, index) => shiftDay(monday, index)));
  }
  return weeks;
}

/** The phone's screen, as data: the first month with anything free, its first free day and hour. */
export function clientPreview(input: PreviewInput): ClientPreview {
  if (input.policy.selfBookingEnabled !== true) return { kind: 'off' };
  if (!input.zonePublished) return { kind: 'noZone' };
  const byDay = bookableStarts(input);
  const day = [...byDay.keys()].sort()[0];
  const starts = day === undefined ? undefined : byDay.get(day);
  const start = starts?.[0];
  if (day === undefined || starts === undefined || start === undefined) return { kind: 'empty' };

  const month = `${day.slice(0, 7)}-01`;
  const weeks = monthWeeks(month).map((week) =>
    week.map((entry): PreviewCell => {
      if (entry.slice(0, 7) !== month.slice(0, 7)) {
        return { day: null, density: 'none', selected: false };
      }
      const free = byDay.get(entry)?.length ?? 0;
      const density: PreviewDensity =
        entry < input.today
          ? 'past'
          : free === 0
            ? 'none'
            : free <= FEW_FREE_STARTS
              ? 'few'
              : 'many';
      return { day: entry, density, selected: entry === day };
    }),
  );
  const length = input.slots.durationMinutes;
  return {
    kind: 'ready',
    month,
    weeks,
    day,
    starts,
    start,
    endMs: start.startMs + length * MINUTE_MS,
    cancelFreeUntilMs:
      input.policy.customerCancellationAllowed === true
        ? start.startMs - (input.policy.cancellationWindowHours ?? 0) * HOUR_MS
        : null,
  };
}
